(() => {
  if (customElements.get('vzy-purchase-panel')) return;

  customElements.define('vzy-purchase-panel', class VzyPurchasePanel extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.timers = new Set();
      this.klarnaTimers = new Set();
      this.klarnaGeneration = 0;
      this.variants = this.readVariants();
      this.variantMap = new Map(this.variants.map((variant) => [String(variant.id), variant]));
      this.resolveRoots();
      if (!this.root || !this.form) {
        this.scheduleRetry(80);
        return;
      }
      this.bind();
      this.refresh();
      [100, 350, 800].forEach((delay) => this.scheduleRetry(delay));
    }

    disconnectedCallback() {
      this.klarnaGeneration += 1;
      this.abortController?.abort();
      this.variantObserver?.disconnect();
      this.klarnaObserver?.disconnect();
      this.schemeObserver?.disconnect();
      this.klarnaRootObserver?.disconnect();
      this.klarnaMessageObserver?.disconnect();
      this.klarnaMessageObserver = null;
      this.klarnaMessageRoot = null;
      this.klarnaRatePlacement = null;
      window.cancelAnimationFrame(this.frame);
      this.timers?.forEach((timer) => window.clearTimeout(timer));
      this.timers?.clear();
      this.klarnaTimers?.clear();
      this.abortController = null;
      this.variantObserver = null;
      this.klarnaObserver = null;
      this.schemeObserver = null;
      this.klarnaRootObserver = null;
      this.klarnaAppNotification = null;
      this.klarnaRefreshNotification = null;
      this.klarnaWrapper = null;
      this.currentProductState = null;
      this.lastKlarnaAmount = null;
      this.bound = false;
    }

    resolveRoots() {
      this.root = document.getElementById(this.dataset.vzyMainId);
      this.form = document.getElementById(this.dataset.vzyFormId);
    }

    readVariants() {
      try {
        return JSON.parse(this.querySelector('[data-vzy-purchase-variants]')?.textContent || '[]');
      } catch (error) {
        return [];
      }
    }

    scheduleRetry(delay) {
      const timer = window.setTimeout(() => {
        this.timers.delete(timer);
        if (!this.isConnected) return;
        this.resolveRoots();
        if (!this.root || !this.form) return;
        if (!this.bound) this.bind();
        this.refresh();
      }, delay);
      this.timers.add(timer);
    }

    bind() {
      if (this.bound || !this.root || !this.form) return;
      this.bound = true;
      const signal = this.abortController.signal;
      this.darkPreference = window.matchMedia('(prefers-color-scheme: dark)');
      this.darkPreference.addEventListener('change', () => this.syncKlarnaTheme(), { signal });
      this.schemeObserver = new MutationObserver(() => this.syncKlarnaTheme());
      this.schemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-scheme'] });
      this.klarnaRootObserver = new MutationObserver((records) => {
        const inserted = records.some((record) => [...record.addedNodes].some((node) => (
          node.nodeType === 1 && (node.matches?.('klarna-placement') || node.querySelector?.('klarna-placement'))
        )));
        if (!inserted) return;
        this.discoverStructure();
        if (this.lastKlarnaAmount > 0) this.applyKlarnaAmount(this.lastKlarnaAmount);
      });
      this.klarnaRootObserver.observe(this.root, { childList: true, subtree: true });
      this.root.addEventListener('input', (event) => this.scheduleSync(event), { signal });
      this.root.addEventListener('change', (event) => this.scheduleSync(event), { signal });
      this.root.addEventListener('vzy:product-state', (event) => {
        this.currentProductState = event.detail || null;
        window.cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.sync(event);
      }, { signal });

      const variantInput = this.variantInput();
      if (variantInput) {
        this.variantObserver = new MutationObserver(() => this.scheduleSync());
        this.variantObserver.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    variantInput() { return this.form?.querySelector('input[name="id"]') || null; }
    quantityInput() { return this.form?.querySelector('input[name="quantity"]') || null; }

    discoverStructure() {
      if (!this.root) return;
      const adapter = window.VzyDigital17Adapter;
      if (!adapter?.resolvePurchase) return;
      this.root.setAttribute('data-vzy-purchase-root', '');
      const nodes = adapter.resolvePurchase(this.root, this);
      const { benefitsWrapper, price, installment, packageWrapper, promoWrapper, actions, klarnaPlacement: klarna, klarnaWrapper } = nodes;
      benefitsWrapper?.setAttribute('data-vzy-purchase-benefits', '');

      const priceBadgeSource = this.querySelector(':scope > [data-vzy-price-badges]');
      let priceBadges = price?.querySelector(':scope > [data-vzy-price-badges]') || null;
      if (price && priceBadgeSource && !priceBadges) {
        priceBadges = priceBadgeSource.cloneNode(true);
        priceBadges.hidden = false;
        priceBadges.removeAttribute('data-vzy-price-badges-source');
        priceBadges.setAttribute('data-vzy-price-badges-rendered', '');
        price.append(priceBadges);
      }

      price?.setAttribute('data-vzy-purchase-price', '');
      installment?.setAttribute('data-vzy-purchase-installment', '');
      if (installment && !installment.textContent.trim()) installment.hidden = true;
      packageWrapper?.setAttribute('data-vzy-purchase-packages', '');
      promoWrapper?.setAttribute('data-vzy-purchase-promo', '');
      actions?.setAttribute('data-vzy-purchase-actions', '');
      klarnaWrapper?.setAttribute('data-vzy-purchase-klarna', '');

      if (klarnaWrapper && klarna) {
        this.ensureKlarnaSummary(klarnaWrapper, klarna);
        if (this.klarnaWrapper !== klarnaWrapper) this.observeKlarna(klarnaWrapper);
      }
    }

    ensureKlarnaSummary(wrapper, placement) {
      this.syncKlarnaTheme(placement);
      let summary = wrapper.querySelector(':scope > [data-vzy-klarna-summary]');
      if (!summary) {
        summary = document.createElement('div');
        summary.className = 'vzy-klarna-summary';
        summary.setAttribute('data-vzy-klarna-summary', '');
        summary.setAttribute('role', 'note');
      }
      if (summary.dataset.vzyKlarnaVersion !== '5') {
        summary.hidden = true;
        summary.dataset.vzyKlarnaVersion = '5';
        summary.removeAttribute('aria-label');
        summary.innerHTML = `
          <span class="vzy-klarna-summary__heading">Plătește în 3 rate egale</span>
          <span class="vzy-klarna-summary__rate" aria-hidden="true">
            <span class="vzy-klarna-summary__spoken visually-hidden"></span>
            <span class="vzy-klarna-summary__number" aria-hidden="true"><span data-vzy-klarna-whole></span><sup data-vzy-klarna-fraction></sup></span>
            <span class="vzy-klarna-summary__units" aria-hidden="true"><span>lei / lună</span><strong>fără dobândă</strong></span>
          </span>
        `;
      }
      placement.removeAttribute('aria-hidden');
      if (summary.parentElement !== wrapper || summary.nextElementSibling !== placement) wrapper.insertBefore(summary, placement);
      this.observeKlarnaMessage(placement);
    }

    observeKlarnaMessage(placement) {
      const attach = () => {
        if (!this.isConnected || !this.root?.contains(placement)) return;
        const shadow = placement.shadowRoot;
        if (!shadow) return;
        if (this.klarnaMessageRoot !== shadow) {
          this.klarnaMessageObserver?.disconnect();
          this.klarnaMessageRoot = shadow;
          this.klarnaRatePlacement = placement;
          this.klarnaMessageObserver = new MutationObserver(() => this.syncKlarnaRate(placement));
          this.klarnaMessageObserver.observe(shadow, { childList: true, subtree: true, characterData: true });
        }
        this.syncKlarnaRate(placement);
      };
      if (placement.shadowRoot) attach();
      else if (this.klarnaRatePlacement !== placement) {
        this.klarnaRatePlacement = placement;
        customElements.whenDefined('klarna-placement').then(attach);
      }
    }

    syncKlarnaRate(placement = this.klarnaRatePlacement) {
      if (!placement || !this.root?.contains(placement)) return;
      const wrapper = placement.closest('[data-vzy-purchase-klarna]');
      const summary = wrapper?.querySelector('[data-vzy-klarna-summary]');
      if (!summary) return;
      const text = placement.shadowRoot?.querySelector('[part="osm-message"]')?.textContent.replace(/\s+/g, ' ').trim() || '';
      const match = text.match(/^3 plăți de ([\d. ]+),(\d{2})\s*lei fără dobândă cu Klarna(?:\s|$)/i);
      const total = Number(placement.getAttribute('data-purchase-amount'));
      const expected = Number(this.lastKlarnaAmount || total);
      const cents = match ? Number(match[1].replace(/[. ]/g, '')) * 100 + Number(match[2]) : 0;
      const ready = Boolean(match && total === expected && total > 0 && Math.abs(cents - total / 3) < 1);
      if (ready) {
        const whole = match[1].replace(/ /g, '.');
        const setText = (selector, value) => { const node = summary.querySelector(selector); if (node.textContent !== value) node.textContent = value; };
        setText('[data-vzy-klarna-whole]', whole);
        setText('[data-vzy-klarna-fraction]', ',' + match[2]);
        setText('.vzy-klarna-summary__spoken', `${whole},${match[2]} lei pe lună, fără dobândă.`);
      }
      wrapper.toggleAttribute('data-vzy-klarna-rate-ready', ready);
      placement.toggleAttribute('data-vzy-klarna-rate-ready', ready);
      summary.hidden = !ready;
    }

    syncKlarnaTheme(placement) {
      const scheme = document.documentElement.dataset.scheme;
      const dark = scheme === 'dark' || ((!scheme || scheme === 'auto') && this.darkPreference?.matches);
      const theme = dark ? 'dark' : 'default';
      const placements = placement ? [placement] : this.root?.querySelectorAll('klarna-placement') || [];
      placements.forEach((item) => {
        // Klarna owns the amount, message and explanation dialog; only select its native theme.
        if (item.getAttribute('data-theme') !== theme) item.setAttribute('data-theme', theme);
      });
    }

    observeKlarna(wrapper) {
      this.klarnaObserver?.disconnect();
      this.klarnaWrapper = wrapper;
      this.klarnaObserver = new MutationObserver((records) => {
        const placementAdded = records.some((record) => [...record.addedNodes].some((node) => (
          node.nodeType === 1
          && (node.matches?.('klarna-placement') || node.querySelector?.('klarna-placement'))
        )));
        const amountChanged = records.some((record) => (
          record.type === 'attributes'
          && record.attributeName === 'data-purchase-amount'
          && record.target.matches?.('klarna-placement')
        ));
        const themeChanged = records.some((record) => record.type === 'attributes' && record.attributeName === 'data-theme');
        if (!placementAdded && !amountChanged && !themeChanged) return;
        const placement = wrapper.querySelector('klarna-placement');
        if (!placement) return;
        this.syncKlarnaTheme(placement);
        if (placementAdded) this.ensureKlarnaSummary(wrapper, placement);
        this.syncKlarnaRate(placement);
        if (this.lastKlarnaAmount > 0) {
          const value = String(Math.round(this.lastKlarnaAmount));
          if (placement.getAttribute('data-purchase-amount') !== value) placement.setAttribute('data-purchase-amount', value);
          placement.removeAttribute('aria-hidden');
        }
      });
      this.klarnaObserver.observe(wrapper, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-purchase-amount', 'data-theme']
      });
    }

    syncNativeStepper() {
      const input = this.quantityInput();
      const wrapper = input?.closest('quantity-input');
      if (!input || !wrapper) return;
      const quantity = Number(input.value || input.min || input.dataset.min || 1);
      const minimum = Number(input.min || input.dataset.min || 1);
      const maximum = Number(input.max || input.dataset.max || 0);
      const states = [
        [wrapper.querySelector('button[name="minus"]'), quantity <= minimum],
        [wrapper.querySelector('button[name="plus"]'), maximum > 0 && quantity >= maximum]
      ];
      states.forEach(([button, disabled]) => {
        if (!button) return;
        button.disabled = disabled;
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        button.classList.toggle('disabled', disabled);
      });
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const locale = this.dataset.vzyLocale || 'ro-RO';
      const currency = this.dataset.vzyCurrency || 'RON';
      try {
        if (currency === 'RON') return `${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} lei`;
        return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2).replace('.', ',')} lei`;
      }
    }

    syncOfferBadges(variant) {
      if (!variant) return;
      const price = Number(variant.price || 0);
      const compareAt = Number(variant.compare_at_price || 0);
      const discount = compareAt > price && compareAt > 0 ? Math.round(((compareAt - price) / compareAt) * 100) : 0;
      const scope = this.root || this;
      scope.querySelectorAll('[data-vzy-sale-badge]').forEach((badge) => { badge.hidden = discount <= 0; });
      const discountValue = scope.querySelector('[data-vzy-discount-value]');
      if (discountValue) discountValue.textContent = `${discount}%`;
    }

    applyKlarnaAmount(amount) {
      if (!Number.isFinite(amount) || amount <= 0 || !this.root) return;
      const value = String(Math.round(amount));
      this.root.dataset.vzyPurchaseAmount = value;
      this.syncKlarnaRate();
      let amountChanged = false;
      this.root.querySelectorAll('klarna-placement').forEach((placement) => {
        if (placement.getAttribute('data-purchase-amount') !== value) {
          amountChanged = true;
          placement.setAttribute('data-purchase-amount', value);
        }
        placement.removeAttribute('aria-hidden');
      });
      // Retry late SDKs or real amount drift without repeatedly rebuilding a stable widget.
      const notify = (owner, method, key, args) => {
        const fn = owner?.[method];
        if (typeof fn !== 'function') return;
        const previous = this[key];
        if (!amountChanged && previous?.amount === value && previous.fn === fn) return;
        try {
          fn.apply(owner, args);
          this[key] = { amount: value, fn };
        } catch (error) {}
      };
      notify(window.KOSMApp, 'updatePurchaseAmount', 'klarnaAppNotification', [Number(value)]);
      notify(window.Klarna?.OnsiteMessaging, 'refresh', 'klarnaRefreshNotification', []);
    }

    syncKlarna(amount) {
      if (!Number.isFinite(amount) || amount <= 0) return;
      const nextAmount = Math.round(amount);
      if (nextAmount === this.lastKlarnaAmount) {
        this.applyKlarnaAmount(nextAmount);
        return;
      }
      this.lastKlarnaAmount = nextAmount;
      this.klarnaGeneration += 1;
      const generation = this.klarnaGeneration;
      this.klarnaTimers.forEach((timer) => {
        window.clearTimeout(timer);
        this.timers.delete(timer);
      });
      this.klarnaTimers.clear();

      [0, 500, 3000, 6000].forEach((delay) => {
        if (delay === 0) {
          this.applyKlarnaAmount(this.lastKlarnaAmount);
          return;
        }
        const timer = window.setTimeout(() => {
          this.klarnaTimers.delete(timer);
          this.timers.delete(timer);
          if (this.isConnected && generation === this.klarnaGeneration) this.applyKlarnaAmount(this.lastKlarnaAmount);
        }, delay);
        this.klarnaTimers.add(timer);
        this.timers.add(timer);
      });
    }

    scheduleSync(event) {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.sync(event));
    }

    sync(event) {
      this.discoverStructure();
      this.syncNativeStepper();
      const variantId = String(this.variantInput()?.value || '');
      const variant = this.variantMap.get(variantId)
        || window.VzyDigital17Adapter?.currentVariant?.(this.root, variantId)
        || null;
      if (!variant) {
        this.dataset.vzyVariantState = 'unresolved';
        (this.root || this).querySelectorAll('[data-vzy-sale-badge]').forEach((badge) => { badge.hidden = true; });
        return;
      }
      this.dataset.vzyVariantState = 'ready';
      this.syncOfferBadges(variant);
      const state = event?.detail?.totalCents ? event.detail : this.currentProductState;
      const quantity = Math.max(1, Math.round(Number(state?.quantity || this.quantityInput()?.value || 1)));
      const total = Number(state?.totalCents || 0) || (Number(variant.price || 0) * quantity);
      this.syncKlarna(total);
    }

    refresh() {
      this.discoverStructure();
      this.sync();
    }
  });
})();

